import {
  type AgentRuntime,
  type Memory,
  type Provider,
  type State,
  logger,
} from "@elizaos/core";
import axios from "axios";
import { getApiConfig, validateCoingeckoConfig } from "../environment";

interface CoinItem {
  id: string;
  symbol: string;
  name: string;
}

const CACHE_KEY = "coingecko:coins";
const CACHE_TTL = 5 * 60; // 5 minutes
const MAX_RETRIES = 3;

async function fetchCoins(
  runtime: AgentRuntime,
  includePlatform = false
): Promise<CoinItem[]> {
  const config = await validateCoingeckoConfig(runtime);
  const { baseUrl, apiKey, headerKey } = getApiConfig(config);

  const response = await axios.get<CoinItem[]>(`${baseUrl}/coins/list`, {
    params: {
      include_platform: includePlatform,
    },
    headers: {
      accept: "application/json",
      [headerKey]: apiKey,
    },
    timeout: 5000, // 5 second timeout
  });

  if (!response.data?.length) {
    throw new Error("Invalid coins data received");
  }

  return response.data;
}

async function fetchWithRetry(
  runtime: AgentRuntime,
  includePlatform = false
): Promise<CoinItem[]> {
  let lastError: Error | null = null;

  for (let i = 0; i < MAX_RETRIES; i++) {
    try {
      return await fetchCoins(runtime, includePlatform);
    } catch (error) {
      lastError = error;
      logger.error(`Coins fetch attempt ${i + 1} failed:`, error);
      await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
    }
  }

  throw lastError || new Error("Failed to fetch coins after multiple attempts");
}

// Simple in-memory cache
const cache: { data?: CoinItem[]; timestamp?: number } = {};

async function getCoins(
  runtime: AgentRuntime,
  includePlatform = false
): Promise<CoinItem[]> {
  try {
    // Check in-memory cache
    if (
      cache.data &&
      cache.timestamp &&
      Date.now() - cache.timestamp < CACHE_TTL * 1000
    ) {
      return cache.data;
    }

    // Fetch fresh data
    const coins = await fetchWithRetry(runtime, includePlatform);

    // Update cache
    cache.data = coins;
    cache.timestamp = Date.now();

    return coins;
  } catch (error) {
    logger.error("Error fetching coins:", error);
    throw error;
  }
}

function formatCoinsContext(coins: CoinItem[]): string {
  const popularCoins = [
    "bitcoin",
    "ethereum",
    "binancecoin",
    "ripple",
    "cardano",
    "solana",
    "polkadot",
    "dogecoin",
  ];

  const popular = coins
    .filter((c) => popularCoins.includes(c.id))
    .map((c) => `${c.name} (${c.symbol.toUpperCase()}) - ID: ${c.id}`);

  return `
Available cryptocurrencies:

Popular coins:
${popular.map((c) => `- ${c}`).join("\n")}

Total available coins: ${coins.length}

You can use these coin IDs when querying specific cryptocurrency data.
`.trim();
}

export const coinsProvider: Provider = {
  name: "COINS_PROVIDER",
  // eslint-disable-next-line
  get: async (runtime: AgentRuntime, message: Memory, state: State) => {
    try {
      const coins = await getCoins(runtime);
      return {
        text: formatCoinsContext(coins),
        data: {
          coins: coins,
        },
      };
    } catch (error) {
      logger.error("Coins provider error:", error);
      return {
        text: "Cryptocurrency list is temporarily unavailable. Please try again later.",
      };
    }
  },
};

// Helper function for actions to get raw coins data
export async function getCoinsData(
  runtime: AgentRuntime,
  includePlatform = false
): Promise<CoinItem[]> {
  return getCoins(runtime, includePlatform);
}

// Helper function to clear cache (mainly for testing)
export function clearCoinsCache(): void {
  cache.data = undefined;
  cache.timestamp = undefined;
}
